import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const EditRegistration: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    vehicle_type: user?.vehicle?.vehicle_type || 'motorbike',
    license_plate: user?.vehicle?.license_plate || '',
    capacity_kg: user?.vehicle?.capacity_kg?.toString() || '',
    make: user?.vehicle?.make || '',
    model: user?.vehicle?.model || '',
    year: user?.vehicle?.year?.toString() || '',
    color: user?.vehicle?.color || '',
    bank_name: user?.bank_info?.bank_name || '',
    account_number: user?.bank_info?.account_number || '',
    account_name: user?.bank_info?.account_name || '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    // Redirect if not a dispatcher or if already approved
    if (!user || user.role !== 'rider') {
      navigate('/');
    }
    if (user && user.approval_status === 'approved') {
      navigate('/rider-dashboard');
    }
  }, [user, navigate]);

  const getCapacityUnit = () => {
    const heavyVehicles = ['12_wheeler', '18_wheeler', 'pickup_truck'];
    return heavyVehicles.includes(formData.vehicle_type) ? 'tonnes' : 'kg';
  };

  const isLicensePlateRequired = () => {
    return formData.vehicle_type !== 'bicycle';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords if changing
    if (formData.password || formData.confirmPassword) {
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
    }

    if (!formData.capacity_kg) {
      setError(`Please provide vehicle capacity in ${getCapacityUnit()}`);
      return;
    }

    if (isLicensePlateRequired() && !formData.license_plate) {
      setError('Please provide license plate for this vehicle type');
      return;
    }

    if (!formData.make || !formData.color) {
      setError('Please provide vehicle make and color');
      return;
    }

    if (!formData.bank_name || !formData.account_number || !formData.account_name) {
      setError('Please provide complete bank information');
      return;
    }

    setLoading(true);

    try {
      const updateData: any = {
        vehicle_type: formData.vehicle_type,
        license_plate: formData.license_plate || '',
        capacity_kg: parseFloat(formData.capacity_kg),
        make: formData.make,
        model: formData.model || null,
        year: formData.year ? parseInt(formData.year) : null,
        color: formData.color,
        bank_name: formData.bank_name,
        account_number: formData.account_number,
        account_name: formData.account_name,
      };

      // Only include password if it was entered
      if (formData.password) {
        updateData.password = formData.password;
      }

      await api.put('/auth/dispatcher/update-registration', updateData);
      
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/rider-dashboard');
        window.location.reload();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update registration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <div className="bg-green-50 border border-green-200 rounded-volexx p-8">
            <div className="bg-green-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <svg className="h-12 w-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Updated!</h2>
            <p className="text-gray-600">
              Your registration has been resubmitted for admin review. Redirecting to dashboard...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/rider-dashboard')}
            className="flex items-center text-volexx-blue-600 hover:text-volexx-blue-700 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Update Registration
          </h1>
          <p className="mt-2 text-gray-600">
            Update your information and resubmit for admin approval
          </p>
        </div>

        {/* Rejection Reason Display */}
        {user?.rejection_reason && (
          <div className="bg-red-50 border border-red-200 rounded-volexx p-4 mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Previous Rejection Reason:</h3>
            <p className="text-gray-700">{user.rejection_reason}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-volexx shadow-volexx p-8">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-volexx">
              {error}
            </div>
          )}

          {/* Vehicle Information */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Vehicle Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vehicle Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                  required
                >
                  <option value="bicycle">Bicycle</option>
                  <option value="motorbike">Motorbike</option>
                  <option value="tricycle">Tricycle</option>
                  <option value="car">Car</option>
                  <option value="van">Van</option>
                  <option value="pickup_truck">Pickup Truck</option>
                  <option value="12_wheeler">12 Wheeler</option>
                  <option value="18_wheeler">18 Wheeler</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  License Plate {isLicensePlateRequired() && <span className="text-red-500">*</span>}
                  {!isLicensePlateRequired() && <span className="text-gray-500 text-xs">(Optional for bicycles)</span>}
                </label>
                <input
                  type="text"
                  name="license_plate"
                  value={formData.license_plate}
                  onChange={handleChange}
                  placeholder="e.g., ABC-123-XY"
                  className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                  required={isLicensePlateRequired()}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Capacity ({getCapacityUnit()}) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="capacity_kg"
                  value={formData.capacity_kg}
                  onChange={handleChange}
                  placeholder={`e.g., ${getCapacityUnit() === 'tonnes' ? '5' : '50'}`}
                  step="0.01"
                  className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Make <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="make"
                  value={formData.make}
                  onChange={handleChange}
                  placeholder="e.g., Honda, Toyota"
                  className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  placeholder="e.g., Accord, Camry"
                  className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="number"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  placeholder="e.g., 2020"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  placeholder="e.g., Black, White"
                  className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>

          {/* Bank Information */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Bank Information</h2>
            <p className="text-red-600 text-sm font-semibold mb-4 font-poppins">
              ⚠️ Warning: Please ensure your bank information is accurate. Earnings will be paid to this account.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleChange}
                  placeholder="e.g., First Bank"
                  className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="account_number"
                  value={formData.account_number}
                  onChange={handleChange}
                  placeholder="e.g., 0123456789"
                  className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="account_name"
                  value={formData.account_name}
                  onChange={handleChange}
                  placeholder="Full name as on bank account"
                  className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>

          {/* Password Update (Optional) */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Change Password <span className="text-gray-500 text-sm font-normal">(Optional)</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Leave blank to keep current"
                  className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm new password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/rider-dashboard')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-volexx font-semibold hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-volexx-orange-600 hover:bg-volexx-orange-700 text-white rounded-volexx font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Submitting...' : 'Resubmit for Approval'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRegistration;
